import { heartFrontDefinition } from "../../../cloudflare/src/game/heart-front-definition";
import { HEART_FRONT1_CLEARING_IDS } from "../../../cloudflare/src/game/heart-front1-route";
import type {
  SiegeCapability,
  SiegeClearingId,
  SiegeCommand,
  SiegeEmptyPayload,
  SiegeErrorCode,
  SiegePillarId,
  SiegeSnapshot,
  SiegeSocketEvent,
  SiegeUiAction,
} from "../types";
import type { VaelorApi } from "./api";

export const HEART_FRONT_ROOM_ID = "heart-front-1" as const;
export const SIEGE_HEARTBEAT_INTERVAL_MS = 20_000;

export interface SiegeTransportHandlers {
  onEvent: (event: SiegeSocketEvent) => void;
  onOpen: () => void;
  onClose: (event: CloseEvent) => void;
  onError: () => void;
  onInvalidEvent?: () => void;
  onTicketExpired?: () => void;
}

export type SiegeSocketFactory = (url: string, protocols: string[]) => WebSocket;

const OPEN_STATE = 1;
const CLOSING_STATE = 2;
const CAPABILITIES: readonly SiegeCapability[] = [
  "siege.subscribe",
  "siege.heartbeat",
  "siege.pillar.hold",
  "siege.pillar.release",
  "siege.guardian.posture",
  "siege.assembly.join",
  "siege.assembly.withdraw",
];
const CAPABILITY_SET = new Set<string>(CAPABILITIES);
const ERROR_CODES: readonly SiegeErrorCode[] = [
  "player_not_maintaining_camp",
  "player_already_holds_pillar",
  "pillar_held",
  "pillar_not_owner",
  "pillar_command_invalid",
  "pillar_command_conflict",
  "guardian_unavailable",
  "guardian_not_pillar_holder",
  "guardian_posture_invalid",
  "guardian_command_conflict",
  "guardian_choice_already_recorded",
  "guardian_subdued",
  "assembly_ineligible",
  "assembly_sealed",
  "assembly_capacity_reached",
  "assembly_already_joined",
  "assembly_not_joined",
  "assembly_command_invalid",
  "assembly_command_conflict",
  "assembly_receipt_capacity",
  "command_not_allowed",
  "message_invalid",
  "authentication_expired",
  "pillar_invalid",
  "command_invalid",
  "command_id_invalid",
  "command_type_invalid",
  "command_date_invalid",
  "command_payload_invalid",
];
const ERROR_CODE_SET = new Set<string>(ERROR_CODES);

export function parseSiegeSnapshot(value: unknown): SiegeSnapshot | null {
  const source = asRecord(value);
  if (
    !source ||
    !hasExactKeys(source, [
      "type",
      "schema",
      "roomId",
      "revision",
      "connectionCount",
      "participantCount",
      "self",
      "assembly",
      "selfAssembly",
      "assault",
      "selfAssault",
      "capabilities",
      "gameplayReady",
      "front",
    ]) ||
    source.type !== "siege.snapshot" ||
    source.schema !== "vaelor-siege-front/v1" ||
    !isNonEmptyString(source.roomId) ||
    !isNonNegativeInteger(source.revision) ||
    !isNonNegativeInteger(source.connectionCount) ||
    !isNonNegativeInteger(source.participantCount) ||
    source.gameplayReady !== true
  ) {
    return null;
  }

  const self = asRecord(source.self);
  const selfShape = self && hasExactKeys(self, [
    "authenticated",
    "maintainsCamp",
    "pillar",
    "guardianChosen",
    "clearingId",
  ])
    ? "current"
    : self && hasExactKeys(self, ["authenticated", "maintainsCamp", "pillar", "guardianChosen"])
      ? "legacy"
      : null;
  if (
    !self ||
    !selfShape ||
    self.authenticated !== true ||
    typeof self.maintainsCamp !== "boolean" ||
    typeof self.guardianChosen !== "boolean" ||
    (self.pillar !== null && self.pillar !== "left" && self.pillar !== "right")
  ) {
    return null;
  }
  const clearingId = selfShape === "legacy"
    ? null
    : readSiegeClearingId(self.clearingId, source.roomId);
  if (clearingId === undefined) return null;

  const capabilities = readCapabilities(source.capabilities);
  const front = readFront(source.front);
  const assembly = readAssembly(source.assembly);
  const selfAssembly = readSelfAssembly(source.selfAssembly);
  const assault = readAssault(source.assault);
  const selfAssault = readSelfAssault(source.selfAssault);
  if (!capabilities || !front || !assembly || !selfAssembly || !assault || !selfAssault) return null;
  if (
    front.gate.assaultReady !== (
      front.gate.state === "vulnerable" &&
      front.guardian.state === "subdued" &&
      assembly.status === "sealed"
    ) || selfAssembly.joined !== (selfAssembly.columnId !== null) ||
    selfAssembly.canWithdraw !== (selfAssembly.joined && assembly.status !== "sealed") ||
    (selfAssembly.columnId !== null && !assembly.columns.includes(selfAssembly.columnId)) ||
    assault.settledCount > assault.columnCount ||
    (selfAssault.status === "settled") !== (selfAssault.report !== null) ||
    (!selfAssault.joined && selfAssault.report !== null)
  ) return null;

  return {
    type: "siege.snapshot",
    schema: "vaelor-siege-front/v1",
    roomId: source.roomId,
    revision: source.revision,
    connectionCount: source.connectionCount,
    participantCount: source.participantCount,
    self: {
      authenticated: true,
      maintainsCamp: self.maintainsCamp,
      clearingId,
      pillar: self.pillar,
      guardianChosen: self.guardianChosen,
    },
    assembly,
    selfAssembly,
    assault,
    selfAssault,
    capabilities,
    gameplayReady: true,
    front,
  };
}

export function parseSiegeSocketEvent(value: unknown): SiegeSocketEvent | null {
  if (typeof value !== "string") return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(value) as unknown;
  } catch {
    return null;
  }

  const source = asRecord(parsed);
  if (!source || typeof source.type !== "string") return null;
  if (source.type === "siege.snapshot") return parseSiegeSnapshot(source);
  if (source.type === "siege.error") return parseSiegeError(source);
  if (source.type === "siege.heartbeat.ack") {
    return hasExactKeys(source, ["type"]) ? { type: "siege.heartbeat.ack" } : null;
  }
  return null;
}

export function newestSiegeSnapshot(
  current: SiegeSnapshot | undefined,
  incoming: SiegeSnapshot,
): SiegeSnapshot {
  return current && current.revision >= incoming.revision ? current : incoming;
}

export function commandForSiegeAction(action: SiegeUiAction): SiegeCommand {
  if (action.kind === "assembly") {
    return action.intent === "join"
      ? { type: "siege.assembly.join", payload: {} }
      : { type: "siege.assembly.withdraw", payload: {} };
  }
  if (action.kind === "guardian") {
    return { type: "siege.guardian.posture", payload: { posture: action.posture } };
  }
  return action.intent === "hold"
    ? { type: "siege.pillar.hold", payload: { pillarId: action.pillarId } }
    : { type: "siege.pillar.release", payload: { pillarId: action.pillarId } };
}

export function siegeErrorMessage(code: SiegeErrorCode): string {
  const messages: Record<SiegeErrorCode, string> = {
    player_not_maintaining_camp: "Maintiens d’abord ta propre clairière avant de tenir un Pilier.",
    player_already_holds_pillar: "Une même personne ne peut tenir les deux Piliers. Un second joueur est requis.",
    pillar_held: "Ce Pilier est déjà tenu.",
    pillar_not_owner: "Tu ne tiens pas ce Pilier.",
    pillar_command_invalid: "L’ordre de Pilier est invalide.",
    pillar_command_conflict: "Cet identifiant d’ordre de Pilier a déjà servi à une autre action.",
    guardian_unavailable: "Le Gardien n’est pas disponible sur cette ronde.",
    guardian_not_pillar_holder: "Seul un joueur tenant un Pilier peut choisir.",
    guardian_posture_invalid: "La posture n’a pas été comprise par le serveur.",
    guardian_command_conflict: "Cet identifiant de choix du Gardien a déjà servi à une autre posture.",
    guardian_choice_already_recorded: "Ton choix pour cette ronde est déjà reçu.",
    guardian_subdued: "Le Gardien est déjà maté; aucun nouveau choix n’est attendu.",
    assembly_ineligible: "Une colonne autoritaire maintenue dans la clairière est requise.",
    assembly_sealed: "L’Assemblée est déjà scellée.",
    assembly_capacity_reached: "L’Assemblée a atteint sa capacité de douze colonnes.",
    assembly_already_joined: "Ta colonne appartient déjà à cette Assemblée.",
    assembly_not_joined: "Ta colonne ne fait pas partie de cette Assemblée.",
    assembly_command_invalid: "L’ordre d’Assemblée est invalide.",
    assembly_command_conflict: "Cet identifiant d’ordre a déjà servi à une autre action.",
    assembly_receipt_capacity: "L’Assemblée ne peut plus accepter d’ordre supplémentaire.",
    command_not_allowed: "Cet ordre n’est pas autorisé.",
    message_invalid: "L’ordre n’a pas été compris par le serveur.",
    authentication_expired: "Ta session a expiré. Reconnecte-toi.",
    pillar_invalid: "Le Pilier demandé n’existe pas.",
    command_invalid: "L’ordre n’a pas été compris par le serveur.",
    command_id_invalid: "L’identifiant de l’ordre est invalide.",
    command_type_invalid: "Le type de l’ordre est invalide.",
    command_date_invalid: "La date de l’ordre est invalide.",
    command_payload_invalid: "Le contenu de l’ordre est invalide.",
  };
  return messages[code];
}

export class SiegeTransport {
  private socket?: WebSocket;
  private heartbeat?: ReturnType<typeof setInterval>;
  private ticketExpiry?: ReturnType<typeof setTimeout>;
  private generation = 0;

  constructor(
    private readonly api: VaelorApi,
    private readonly webSocketBase: string,
    private readonly handlers: SiegeTransportHandlers,
    private readonly socketFactory: SiegeSocketFactory = (url, protocols) =>
      new WebSocket(url, protocols),
  ) {}

  async connect(roomId: string): Promise<void> {
    const generation = ++this.generation;
    this.disposeSocket();
    const ticket = await this.api.siegeSocketTicket(roomId);
    if (generation !== this.generation) return;

    const url = `${this.webSocketBase}/v1/ws/sieges/${encodeURIComponent(roomId)}`;
    const socket = this.socketFactory(url, ticket.protocols);
    this.socket = socket;
    const expiresAtMs = ticket.expiresAt * 1000;
    if (this.handlers.onTicketExpired) {
      const delay = Number.isFinite(expiresAtMs)
        ? Math.max(0, expiresAtMs - Date.now())
        : 0;
      this.ticketExpiry = setTimeout(() => {
        if (
          this.socket !== socket ||
          generation !== this.generation ||
          socket.readyState === OPEN_STATE
        ) return;
        this.generation += 1;
        this.disposeSocket();
        this.handlers.onTicketExpired?.();
      }, delay);
    }

    socket.addEventListener("open", () => {
      if (this.socket !== socket) return;
      if (
        this.handlers.onTicketExpired &&
        Number.isFinite(expiresAtMs) &&
        Date.now() >= expiresAtMs
      ) {
        this.generation += 1;
        this.disposeSocket();
        this.handlers.onTicketExpired();
        return;
      }
      this.clearTicketExpiry();
      this.handlers.onOpen();
      this.requestSnapshot();
      this.clearHeartbeat();
      this.heartbeat = setInterval(() => {
        if (this.socket === socket && socket.readyState === OPEN_STATE) {
          this.send({ type: "siege.heartbeat", payload: emptyPayload() });
        }
      }, SIEGE_HEARTBEAT_INTERVAL_MS);
    });
    socket.addEventListener("message", (event) => {
      const parsed = parseSiegeSocketEvent(event.data);
      if (parsed) {
        this.handlers.onEvent(parsed);
      } else {
        this.handlers.onInvalidEvent?.();
      }
    });
    socket.addEventListener("error", () => {
      if (this.socket === socket) this.handlers.onError();
    });
    socket.addEventListener("close", (event) => {
      if (this.socket !== socket) return;
      this.clearHeartbeat();
      this.clearTicketExpiry();
      this.socket = undefined;
      this.handlers.onClose(event);
    });
  }

  requestSnapshot(): string {
    return this.send({ type: "siege.subscribe", payload: emptyPayload() });
  }

  send(command: SiegeCommand): string {
    const socket = this.socket;
    if (!socket || socket.readyState !== OPEN_STATE) {
      throw new Error("siege_not_connected");
    }
    const commandId = `siege_${crypto.randomUUID()}`;
    socket.send(JSON.stringify({
      commandId,
      type: command.type,
      issuedAt: new Date().toISOString(),
      payload: command.payload,
    }));
    return commandId;
  }

  close(): void {
    this.generation += 1;
    this.disposeSocket();
  }

  private disposeSocket(): void {
    this.clearHeartbeat();
    this.clearTicketExpiry();
    const socket = this.socket;
    this.socket = undefined;
    if (socket && socket.readyState < CLOSING_STATE) {
      socket.close(1000, "front_closed");
    }
  }

  private clearHeartbeat(): void {
    if (this.heartbeat) clearInterval(this.heartbeat);
    this.heartbeat = undefined;
  }

  private clearTicketExpiry(): void {
    if (this.ticketExpiry) clearTimeout(this.ticketExpiry);
    this.ticketExpiry = undefined;
  }
}

function emptyPayload(): SiegeEmptyPayload {
  return {};
}

function parseSiegeError(value: Record<string, unknown>): SiegeSocketEvent | null {
  if (!hasExactKeys(value, ["type", "error"]) || !isSiegeErrorCode(value.error)) {
    return null;
  }
  return { type: "siege.error", error: value.error };
}

function readFront(value: unknown): SiegeSnapshot["front"] | null {
  const source = asRecord(value);
  if (!source || !hasExactKeys(source, ["camp", "pillars", "gate", "guardian"])) return null;

  const camp = asRecord(source.camp);
  if (!camp || !hasExactKeys(camp, ["supplied"]) || typeof camp.supplied !== "boolean") {
    return null;
  }

  const pillars = source.pillars;
  if (!Array.isArray(pillars) || pillars.length !== 2) return null;
  const left = readPillar(pillars[0], "left");
  const right = readPillar(pillars[1], "right");
  if (!left || !right) return null;

  const gate = asRecord(source.gate);
  if (
    !gate ||
    !hasExactKeys(gate, ["state", "assaultReady"]) ||
    (gate.state !== "sealed" && gate.state !== "vulnerable") ||
    typeof gate.assaultReady !== "boolean"
  ) {
    return null;
  }

  const guardian = readGuardian(source.guardian);
  if (!guardian) return null;
  return {
    camp: { supplied: camp.supplied },
    pillars: [left, right] as SiegeSnapshot["front"]["pillars"],
    gate: { state: gate.state, assaultReady: gate.assaultReady },
    guardian,
  };
}

function readAssembly(value: unknown): SiegeSnapshot["assembly"] | null {
  const source = asRecord(value);
  if (!source || !hasExactKeys(source, [
    "status", "minimumColumns", "capacity", "columnCount", "deadlineAtMs", "columns"
  ])) return null;
  if (
    source.status !== "idle" && source.status !== "forming" &&
    source.status !== "countdown" && source.status !== "sealed"
  ) return null;
  if (source.minimumColumns !== 2 || source.capacity !== 12 ||
      !isBoundedInteger(source.columnCount, 0, 12) ||
      (source.deadlineAtMs !== null && !isNonNegativeInteger(source.deadlineAtMs)) ||
      !Array.isArray(source.columns) || source.columns.length !== source.columnCount ||
      !source.columns.every((column) => typeof column === "string" && /^column-\d{3}$/.test(column)) ||
      new Set(source.columns).size !== source.columns.length) return null;
  const shapeValid = source.status === "idle"
    ? source.columnCount === 0 && source.deadlineAtMs === null
    : source.status === "forming"
      ? source.columnCount > 0 && source.columnCount < 2 && source.deadlineAtMs === null
      : source.status === "countdown"
        ? source.columnCount >= 2 && source.deadlineAtMs !== null
        : source.columnCount >= 2 && source.deadlineAtMs === null;
  if (!shapeValid) return null;
  return {
    status: source.status,
    minimumColumns: 2,
    capacity: 12,
    columnCount: source.columnCount,
    deadlineAtMs: source.deadlineAtMs,
    columns: [...source.columns]
  };
}

function readSelfAssembly(value: unknown): SiegeSnapshot["selfAssembly"] | null {
  const source = asRecord(value);
  if (!source || !hasExactKeys(source, ["joined", "columnId", "canWithdraw"]) ||
      typeof source.joined !== "boolean" || typeof source.canWithdraw !== "boolean" ||
      (source.columnId !== null && (typeof source.columnId !== "string" || !/^column-\d{3}$/.test(source.columnId)))) {
    return null;
  }
  return { joined: source.joined, columnId: source.columnId, canWithdraw: source.canWithdraw };
}

function readAssault(value: unknown): SiegeSnapshot["assault"] | null {
  const source = asRecord(value);
  if (!source || !hasExactKeys(source, ["status", "columnCount", "settledCount"]) ||
      !["idle", "locking", "resolving", "settling", "repelled", "breached", "blocked"].includes(String(source.status)) ||
      !isNonNegativeInteger(source.columnCount) || source.columnCount > 12 ||
      !isNonNegativeInteger(source.settledCount) || source.settledCount > source.columnCount) return null;
  return {
    status: source.status as SiegeSnapshot["assault"]["status"],
    columnCount: source.columnCount,
    settledCount: source.settledCount
  };
}

function readCountMap(value: unknown): Record<string, number> | null {
  const source = asRecord(value);
  if (!source || Object.keys(source).length === 0) return null;
  const output: Record<string, number> = {};
  for (const key of Object.keys(source).sort()) {
    const amount = source[key];
    if (!/^[a-z][a-z0-9_-]{0,31}$/.test(key) || !isNonNegativeInteger(amount)) return null;
    output[key] = amount;
  }
  return output;
}

function readSelfAssault(value: unknown): SiegeSnapshot["selfAssault"] | null {
  const source = asRecord(value);
  if (!source || !hasExactKeys(source, ["joined", "status", "report"]) ||
      typeof source.joined !== "boolean" ||
      !["idle", "waiting", "locked", "settled", "blocked"].includes(String(source.status))) return null;
  if (source.report === null) {
    if (source.status === "settled") return null;
    return { joined: source.joined, status: source.status as SiegeSnapshot["selfAssault"]["status"], report: null };
  }
  const report = asRecord(source.report);
  if (!report || source.joined !== true || source.status !== "settled" ||
      !hasExactKeys(report, ["outcome", "reportId", "resolvedAt", "engaged", "rawLosses", "recovered", "losses", "survivors"]) ||
      (report.outcome !== "repelled" && report.outcome !== "breached") ||
      !isNonEmptyString(report.reportId) || typeof report.resolvedAt !== "string" ||
      Number.isNaN(Date.parse(report.resolvedAt))) return null;
  const engaged = readCountMap(report.engaged);
  const rawLosses = readCountMap(report.rawLosses);
  const recovered = readCountMap(report.recovered);
  const losses = readCountMap(report.losses);
  const survivors = readCountMap(report.survivors);
  if (!engaged || !rawLosses || !recovered || !losses || !survivors ||
      JSON.stringify(Object.keys(engaged)) !== JSON.stringify(Object.keys(rawLosses)) ||
      JSON.stringify(Object.keys(engaged)) !== JSON.stringify(Object.keys(recovered)) ||
      JSON.stringify(Object.keys(engaged)) !== JSON.stringify(Object.keys(losses)) ||
      JSON.stringify(Object.keys(engaged)) !== JSON.stringify(Object.keys(survivors))) return null;
  for (const key of Object.keys(engaged)) {
    if (engaged[key] !== losses[key] + survivors[key] || rawLosses[key] !== losses[key] + recovered[key]) return null;
  }
  return {
    joined: true,
    status: "settled",
    report: {
      outcome: report.outcome,
      reportId: report.reportId,
      resolvedAt: report.resolvedAt,
      engaged, rawLosses, recovered, losses, survivors
    }
  };
}

function readGuardian(value: unknown): SiegeSnapshot["front"]["guardian"] | null {
  const source = asRecord(value);
  if (!source || !hasExactKeys(source, ["round", "state", "choices", "lastResult"])) return null;
  if (
    source.round !== "pressure" && source.round !== "opening" && source.round !== "breach"
  ) {
    return null;
  }
  if (
    source.state !== "inactive" && source.state !== "ready" && source.state !== "subdued"
  ) {
    return null;
  }
  if (
    source.lastResult !== "none" && source.lastResult !== "success" && source.lastResult !== "failure"
  ) {
    return null;
  }

  const choices = asRecord(source.choices);
  if (
    !choices ||
    !hasExactKeys(choices, ["received", "complete"]) ||
    !isBoundedInteger(choices.received, 0, 2) ||
    typeof choices.complete !== "boolean"
  ) {
    return null;
  }

  return {
    round: source.round,
    state: source.state,
    choices: { received: choices.received, complete: choices.complete },
    lastResult: source.lastResult,
  };
}

function readPillar(
  value: unknown,
  expectedId: SiegePillarId,
): { id: "left"; held: boolean } | { id: "right"; held: boolean } | null {
  const source = asRecord(value);
  if (!source || !hasExactKeys(source, ["id", "held"]) || source.id !== expectedId || typeof source.held !== "boolean") {
    return null;
  }
  return expectedId === "left"
    ? { id: "left", held: source.held }
    : { id: "right", held: source.held };
}

function readSiegeClearingId(
  value: unknown,
  roomId: unknown,
): SiegeClearingId | null | undefined {
  if (value === null) return null;
  if (typeof value !== "string" || typeof roomId !== "string") return undefined;
  const definition = heartFrontDefinition(roomId);
  if (!definition) return undefined;
  const clearingIds = definition.frontId === "heart-front-1"
    ? HEART_FRONT1_CLEARING_IDS
    : definition.clearingIds;
  return clearingIds.includes(value) ? value : undefined;
}

function readCapabilities(value: unknown): SiegeCapability[] | null {
  if (!Array.isArray(value) || value.length !== CAPABILITIES.length) return null;
  const capabilities: SiegeCapability[] = [];
  for (const candidate of value) {
    if (typeof candidate !== "string" || !CAPABILITY_SET.has(candidate)) {
      return null;
    }
    const capability = candidate as SiegeCapability;
    if (capabilities.includes(capability)) return null;
    capabilities.push(capability);
  }
  return CAPABILITIES.every((capability) => capabilities.includes(capability)) ? capabilities : null;
}

function isSiegeErrorCode(value: unknown): value is SiegeErrorCode {
  return typeof value === "string" && ERROR_CODE_SET.has(value);
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null;
}

function hasExactKeys(source: Record<string, unknown>, expected: readonly string[]): boolean {
  if (Object.keys(source).length !== expected.length) return false;
  const expectedSet = new Set(expected);
  return Object.keys(source).every((key) => expectedSet.has(key));
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.length > 0;
}

function isNonNegativeInteger(value: unknown): value is number {
  return typeof value === "number" && Number.isInteger(value) && value >= 0;
}

function isBoundedInteger(value: unknown, minimum: number, maximum: number): value is number {
  return typeof value === "number" && Number.isInteger(value) && value >= minimum && value <= maximum;
}
